import { FeatureFlag } from '@/constants/feature-flags.ts';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import * as Sentry from '@sentry/react';
import arrayShuffle from 'array-shuffle';
import clsx from 'clsx';
import { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import NonBookableAlert from '@/components/NonBookableAlert.jsx';
import ProfessionalResult from '@/components/ProfessionalResult.jsx';
import RenderEmptyBook from '@/components/RenderEmptyBook.jsx';
import { CategoryBrowser } from '@/components/Search/category-browser.tsx';
import ChainNoPreferenceCard from '@/components/Search/chain-no-preference-card.tsx';

import { ContextProvider } from '@/context/ProjectContext.jsx';

import { useBrowsableCategories } from '@/hooks/use-category-browser.ts';
import { useChainConfigs } from '@/hooks/use-chain-configs.ts';
import { useFeatureFlag } from '@/hooks/use-feature-flag.ts';

import { chainPatientTypeAvailability } from '@/utils/chain-config-utils.ts';
import { getActiveMarkedSteps, getEligibleMainPractitioners } from '@/utils/chain-main-practitioner-utils.ts';
import { BookingStep, HealthInsurance, MIN_SEARCH_LENGTH } from '@/utils/constants.js';
import {
    JourneyType,
    fetchAvailableProfessionals,
    getConsentTreatments,
    getInsuranceAvailability,
} from '@/utils/helpers.js';
import { formatInsuranceType, isTreatmentAvailableForInsurance } from '@/utils/insurance-utils.ts';
import { returnToSearchLanding } from '@/utils/search-navigation.ts';

import {
    CLEARED_CHAIN_PAGING,
    CLEARED_CHAIN_SELECTION,
    useBookingStore,
    useConfigStore,
    usePersistedPrismicStore,
    useSearchStore,
    useXundStore,
} from '@/store.js';

import NoTreatmentsMessage from '../Common/NoTreatmentsMessage.jsx';
import EternoSpinner from '../EternoLogoSpinner/eterno-spinner.tsx';
import FilledButton from '../FilledButton.jsx';
import BookWithProfessional from './BookWithProfessional.jsx';
import SearchResultCard, { SearchResultType } from './SearchResultCard.jsx';
import { SearchBackButton } from './search-back-button.tsx';
import { SearchNoResults } from './search-no-results.tsx';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        gap: '2rem',
        transition: 'opacity 0.3s ease',
        boxSizing: 'border-box',
    },
    accordionContainer: {
        overflowX: 'hidden',
    },
    selectionBar: {
        borderRadius: theme.radius.default,
        position: 'sticky',
        bottom: 0,
        left: 0,
        width: '100%',
        background: '#FFF',
        border: theme.border.light,
        padding: '16px 32px',
        marginTop: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        [theme.breakpoints.down('xs')]: {
            padding: '16px',
        },
    },
    resultCategoryContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: '1rem',
    },
    subTitleWrapper: {
        width: '100%',
    },
    resultSubTitle: {
        color: '#111',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 600,
        textTransform: 'uppercase',
        margin: 0,
        opacity: 0.5,
    },
    specialtyTitle: {
        color: '#2e2e2e',
        fontSize: '18px',
        fontStyle: 'normal',
        fontWeight: 700,
        margin: 0,
    },
    resultCardsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
    },
    selectionsWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    selections: {
        margin: 0,
        maxWidth: '30rem',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        [theme.breakpoints.down('xs')]: {
            maxWidth: '10rem',
        },
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '20vh',
    },
    noTreatmentsHeading: {
        padding: '2rem 0',
    },
    availableProfessionalsContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
    },
    shortText: {
        fontSize: '1rem',
        textTransform: 'none',
    },
    chainHeading: {
        ...theme.typography.HL1,
        fontWeight: 'bold',
        textAlign: 'center',
        width: '100%',
    },
}));

const SearchResults = () => {
    const classes = useStyles();
    const history = useHistory();
    const theme = useTheme();
    const { i18n, t } = useTranslation();
    const matchesXs = useMediaQuery(theme.breakpoints.down('xs'));
    const matchesSm = useMediaQuery(theme.breakpoints.down('sm'));
    const { setAppointmentSearch } = useContext(ContextProvider);
    const prismicData = useSearchStore((state) => state.prismicData);
    const search = useSearchStore((state) => state.search);
    const { categories: categoryBrowserCategories, showCategoryBrowser } = useBrowsableCategories();
    const professionals = useSearchStore((state) => state.professionals);
    const treatments = useSearchStore((state) => state.treatments);
    const locations = useSearchStore((state) => state.locations);
    const selectedProfessional = useSearchStore((state) => state.selectedProfessional);
    const selectedLocation = useSearchStore((state) => state.selectedLocation);
    const selectedSpecialty = useSearchStore((state) => state.selectedSpecialty);
    const selectedHealthInsurance = useSearchStore((state) => state.selectedHealthInsurance);
    const selectedTreatments = useSearchStore((state) => state.selectedTreatments);
    const showAvailableProfessionals = useSearchStore((state) => state.showAvailableProfessionals);
    const allDocuments = useSearchStore((state) => state.allDocuments);
    const isOnlineAppointment = useSearchStore((state) => state.isOnlineAppointment);
    const availableProfessionals = useSearchStore((state) => state.availableProfessionals);
    const selectedChainConfig = useSearchStore((state) => state.selectedChainConfig);
    const chainMainPractitioner = useSearchStore((state) => state.chainMainPractitioner);
    const currentCustomer = useConfigStore((state) => state.currentCustomer);
    const {
        [i18n.language]: { mainData, dataSet },
    } = usePersistedPrismicStore((state) => state.bookAppointmentData);
    const {
        [i18n.language]: { professionalProfiles },
    } = usePersistedPrismicStore((state) => state.findAppointmentData);
    const {
        [i18n.language]: { mainData: professionalProfilePrismicData },
    } = usePersistedPrismicStore((state) => state.professionalProfileData);
    const showBookingModal = useBookingStore((state) => state.showBookingModal);
    const [filteredProfessionals, setFilteredProfessionals] = useState([]);
    const [filteredProfessionalTypes, setFilteredProfessionalTypes] = useState([]);
    const [filteredTreatments, setFilteredTreatments] = useState([]);
    const [loading, setLoading] = useState(false);
    const isXundTriageProcessed = useXundStore((state) => state.isXundTriageProcessed);

    const { value: isChainBookingEnabled } = useFeatureFlag(FeatureFlag.IS_CHAIN_BOOKING_ENABLED_FOR_PATIENTS);
    const customerId = currentCustomer?.customer_id?.[0]?.text;
    const chainLocationKey = selectedLocation ?? currentCustomer?.locations?.[0]?.key?.[0]?.text;
    const { chainConfigs } = useChainConfigs(
        Boolean(isChainBookingEnabled && formatInsuranceType(selectedHealthInsurance)),
        customerId,
        chainLocationKey,
        formatInsuranceType(selectedHealthInsurance)
    );

    const formattedSearch = (search ?? '').toLowerCase().trim();
    const isSearchActive = formattedSearch.length >= MIN_SEARCH_LENGTH;

    const filteredChainConfigs = (() => {
        if (!isChainBookingEnabled || chainConfigs.length === 0) return [];

        // Nothing searched yet: browsing belongs to the treatment list, not to this view.
        if (!isSearchActive) return [];

        return chainConfigs.filter((config) => {
            const chainName = config.name[i18n.language] ?? config.name.de;
            return chainName.toLowerCase().includes(formattedSearch);
        });
    })();

    const renderChainCards = () =>
        filteredChainConfigs.map((config) => (
            <SearchResultCard
                key={config.id}
                cardKey={config.id}
                config={config}
                searchResultType={SearchResultType.CHAIN}
            />
        ));

    // Chain main-practitioner page derivations. `markedSteps` and `shared` together drive the
    // four-way branch (0 marked → no-preference only · marked>0 & 0 shared → not bookable ·
    // 1 shared → single card, no competing option (no pre-commit; the patient-type click commits)
    // · >1 shared → named cards + no-preference). `[]` from `shared` is ambiguous on its own, so
    // `markedSteps.length` always disambiguates bookable-vs-not.
    const markedSteps = selectedChainConfig ? getActiveMarkedSteps(selectedChainConfig) : [];
    const sharedMainPractitioners = selectedChainConfig ? getEligibleMainPractitioners(selectedChainConfig) : [];
    const hasMarkedSteps = markedSteps.length > 0;
    const isChainNotBookable = hasMarkedSteps && sharedMainPractitioners.length === 0;
    const showNoPreferenceCard = !hasMarkedSteps || sharedMainPractitioners.length > 1;

    // Re-run the no-shared diagnostic when the chain or its location/insurance context changes (not
    // every render). 0-marked is bookable, so it never fires here. console.error always runs; the
    // Sentry capture is consent-gated in sentry.js.
    useEffect(() => {
        if (!selectedChainConfig || !isChainNotBookable) return;

        const reason = 'no_shared_practitioner';
        console.error('chain not bookable online', {
            chain_id: selectedChainConfig.id,
            location_key: chainLocationKey,
            insurance_type: formatInsuranceType(selectedHealthInsurance),
            reason: reason,
        });
        Sentry.withScope((scope) => {
            scope.setContext('chain', {
                chain_id: selectedChainConfig.id,
                location_key: chainLocationKey,
                insurance_type: formatInsuranceType(selectedHealthInsurance),
                reason: reason,
            });
            Sentry.captureMessage('chain not bookable online', 'warning');
        });
    }, [selectedChainConfig?.id, isChainNotBookable, chainLocationKey, selectedHealthInsurance]);

    const clearChainSelection = () =>
        useSearchStore.setState({
            showAvailableProfessionals: false,
            ...CLEARED_CHAIN_SELECTION,
        });

    const selectNoPreference = (isReturningPatient) => {
        useSearchStore.setState({
            chainIsReturningPatient: isReturningPatient ?? false,
            chainMainPractitionerCandidate: null,
            chainMainPractitioner: { type: 'no_preference' },
            ...CLEARED_CHAIN_PAGING,
        });
        useBookingStore.setState({
            showBookingModal: true,
            currentStep: BookingStep.CHAIN_SUGGESTIONS,
            isChainSuggestionFetching: false,
        });
    };

    const renderChainMainPractitionerPage = () => {
        // Practice name: the location hub title the booking flow uses, falling back to the customer
        // name; logo: the customer icon Header.jsx renders (placeholder when absent).
        const practiceName =
            locations?.[selectedLocation]?.title?.[0]?.text ?? currentCustomer?.customer_name?.[0]?.text ?? '';
        const logoUrl = currentCustomer?.customer_icon_large?.url ?? null;
        const { supportsNew, supportsReturning } = chainPatientTypeAvailability(selectedChainConfig);
        const noPreferenceDoctor = {
            is_for_new_patients: supportsNew,
            is_for_returning_patients: supportsReturning,
        };
        const isNoPreferenceSelected = chainMainPractitioner?.type === 'no_preference';

        return (
            <div className={classes.availableProfessionalsContainer}>
                <SearchBackButton
                    label={professionalProfilePrismicData?.go_back_cta?.[0]?.text}
                    onClick={clearChainSelection}
                />

                <Typography className={classes.chainHeading} data-testid="chain-main-practitioner-heading">
                    {t('ChainMainPractitionerHeading')}
                </Typography>

                {isChainNotBookable ? (
                    <div data-testid="chain-not-bookable">
                        <NonBookableAlert />
                    </div>
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1.5rem',
                            width: '100%',
                        }}
                    >
                        {availableProfessionals.map((availableProfessional) => (
                            <ProfessionalResult
                                key={availableProfessional.id}
                                dataSet={dataSet}
                                mainData={mainData}
                                doctor={availableProfessional}
                                promptOverride={t('ChainNamedPractitionerPrompt')}
                                isSelected={
                                    chainMainPractitioner?.type === 'specific' &&
                                    chainMainPractitioner.id ===
                                        (availableProfessional.practitioner_id ?? availableProfessional.id)
                                }
                            />
                        ))}

                        {showNoPreferenceCard && (
                            <ChainNoPreferenceCard
                                label={t('ChainNoPreferenceCardLabel')}
                                practiceName={practiceName}
                                logoUrl={logoUrl}
                                prompt={t('ChainPracticePrompt')}
                                doctor={noPreferenceDoctor}
                                mainData={mainData}
                                loading={!dataSet}
                                isSelected={isNoPreferenceSelected}
                                onSelectPatientType={selectNoPreference}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    const isTreatmentAvailableForSelectedInsurance = (treatment) =>
        isTreatmentAvailableForInsurance(treatment, selectedHealthInsurance);

    // A treatment with a blank prismic name cannot be shown and one with a blank key cannot be
    // booked, so it is dropped here instead of reaching a match or a card that dereferences it.
    const isTreatmentUsable = (treatment) => Boolean(treatment?.name?.[0]?.text && treatment?.key?.[0]?.text);

    // Three across at most: the rows are wide enough that a fourth column squeezes the names.
    const getGridColumns = (additionalColumns = 0) => {
        if (matchesXs) return `${1 + additionalColumns}`;
        if (matchesSm) return `${2 + additionalColumns}`;
        return `${3 + additionalColumns}`;
    };

    const availableInsurances = useMemo(() => {
        if (!selectedSpecialty) return [];

        const treatmentsArray =
            locations[selectedLocation]?.professional_types[selectedSpecialty]?.services_or_treatments || [];

        const insuranceAvailability = getInsuranceAvailability(treatmentsArray);

        return Object.keys(insuranceAvailability).filter((key) => insuranceAvailability[key]);
    }, [selectedSpecialty, selectedLocation, locations]);

    const startBooking = () => {
        if (currentCustomer?.is_accordion_for_multiple_professionals) {
            useSearchStore.setState({
                showAvailableProfessionals: true,
            });
        } else {
            const consentTreatments = getConsentTreatments({
                selectedTreatments: selectedTreatments,
                treatments: treatments,
                selectedLocation: selectedLocation,
                allDocuments: allDocuments,
                selectedHealthInsurance: selectedHealthInsurance,
            });
            useBookingStore.getState().resetStore();
            useBookingStore.setState({
                selectedProfessional: professionalProfiles.find(
                    (professional) => professional.location_hub.id === currentCustomer.locations[0].prismicId
                ),
                showBookingModal: true,
                consentTreatments: consentTreatments,
                currentStep: consentTreatments.length > 0 ? BookingStep.CONSENT : BookingStep.TIMESLOT_SELECTION,
                isOnlineAppointment: isOnlineAppointment,
            });
        }
    };

    const getAvailableProfessionals = async () => {
        setLoading(true);
        useSearchStore.setState({ availableProfessionals: [] });
        const selectedTreatment = treatments[selectedLocation][Object.keys(selectedTreatments)[0]];

        if (!selectedTreatment) {
            setLoading(false);
            return;
        }

        const response = await fetchAvailableProfessionals(
            selectedLocation,
            selectedTreatment.specialty,
            selectedTreatment.key[0].text,
            selectedHealthInsurance.replace('_', '-'),
            JourneyType.SEARCH_BOOK
        );

        if (!response?.data) {
            setLoading(false);
            return;
        }

        const {
            [i18n.language]: { content: findAppointmentPrismicData },
        } = usePersistedPrismicStore.getState().findAppointmentData;
        setAppointmentSearch({
            location_key: selectedLocation,
            professional_type_key: selectedTreatment.specialty,
            insurance_type_key: selectedHealthInsurance,
            professional_name: null,
            is_for_self: true,
            is_with_prescription: false,
            is_haveImg: false,
            symptoms: [],
            other_symptom: '',
            map_title: findAppointmentPrismicData[selectedLocation]
                ? findAppointmentPrismicData[selectedLocation].title
                : 'Online',
            location_title: findAppointmentPrismicData[selectedLocation]
                ? findAppointmentPrismicData[selectedLocation].eterno_map_title
                : 'Online',
            journey_type: JourneyType.SEARCH_BOOK,
        });

        const professionalsArray = arrayShuffle(
            response.data
                .filter((professional) => professionals[professional.key])
                .map((professional) => ({
                    ...professional,
                    ...professionals[professional.key],
                    specialityType: allDocuments[professionals[professional.key].type.id],
                }))
        );

        useSearchStore.setState({ availableProfessionals: professionalsArray });
        setLoading(false);
    };

    useLayoutEffect(() => {
        // Chains source their pro-list from the config, so skip the treatment availability fetch.
        if (!showAvailableProfessionals || !selectedHealthInsurance || selectedChainConfig) return;
        getAvailableProfessionals();
    }, [showAvailableProfessionals, selectedHealthInsurance, selectedLocation, selectedChainConfig]);

    const searchTagMatch = (searchTagsField) => {
        if (!searchTagsField?.[0]?.text) return false;

        const tagsArray = searchTagsField[0].text
            .toLowerCase()
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);

        return tagsArray.some((tag) => tag.includes(formattedSearch));
    };

    useEffect(() => {
        if (!selectedLocation) return;

        if (!isSearchActive) {
            setFilteredProfessionals([]);
            setFilteredProfessionalTypes([]);

            // If we have selected treatments from Xund, filter them by insurance availability
            const hasSelectedTreatments = Object.values(selectedTreatments).some((value) => value);

            if (hasSelectedTreatments) {
                const treatmentArray = Object.values(treatments[selectedLocation] ?? {});
                setFilteredTreatments(
                    treatmentArray.filter(
                        (treatment) =>
                            isTreatmentUsable(treatment) &&
                            selectedTreatments[treatment.key[0].text] &&
                            isTreatmentAvailableForSelectedInsurance(treatment)
                    )
                );
            } else {
                setFilteredTreatments([]);
            }

            return;
        }

        setFilteredProfessionals(
            Object.values(professionals).filter(
                (professional) =>
                    allDocuments[professional.location_hub.id]?.key[0].text === selectedLocation &&
                    (professional.display_name[0].text.toLowerCase().includes(formattedSearch) ||
                        searchTagMatch(professional.search_tags))
            )
        );
        setFilteredProfessionalTypes(
            Object.values(locations[selectedLocation]?.professional_types).filter(
                (professionalType) =>
                    professionalType.name[0].text.toLowerCase().includes(formattedSearch) ||
                    searchTagMatch(professionalType.search_tags)
            )
        );

        const treatmentArray = Object.values(
            selectedSpecialty
                ? (locations[selectedLocation]?.professional_types[selectedSpecialty]?.services_or_treatments.map(
                      (entry) => ({
                          ...entry,
                          ...entry.service_or_treatment,
                      })
                  ) ?? {})
                : (treatments[selectedLocation] ?? {})
        );

        setFilteredTreatments(
            treatmentArray.filter(
                (treatment) =>
                    isTreatmentUsable(treatment) &&
                    (selectedSpecialty ||
                        treatment.name[0].text.toLowerCase().includes(formattedSearch) ||
                        searchTagMatch(treatment.search_tags)) &&
                    isTreatmentAvailableForSelectedInsurance(treatment)
            )
        );
    }, [search, selectedLocation, selectedHealthInsurance, treatments, selectedSpecialty, selectedTreatments]);

    // The booking modal is a fixed overlay, so the page behind it must not scroll. Nothing else
    // locks the body, so this is the only thing keeping the background still while it is open.
    useEffect(() => {
        document.body.style.overflow = showBookingModal ? 'hidden' : 'unset';

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showBookingModal]);

    if (loading) {
        // The modal backdrop is translucent, so a spinner here would show through next to the modal's own.
        if (showBookingModal) return null;
        return (
            <div className={classes.loadingContainer}>
                <EternoSpinner />
            </div>
        );
    }

    if (showAvailableProfessionals) {
        // A selected chain owns its own render; it must not fall through to the single-appointment
        // empty-state below, which an empty `availableProfessionals` would otherwise trigger.
        if (selectedChainConfig) {
            return renderChainMainPractitionerPage();
        }

        return (
            <div className={classes.availableProfessionalsContainer}>
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                    }}
                >
                    <SearchBackButton
                        label={professionalProfilePrismicData?.go_back_cta?.[0]?.text}
                        onClick={() => returnToSearchLanding(history)}
                    />
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.5rem',
                    }}
                >
                    {availableProfessionals.map((availableProfessional) => (
                        <ProfessionalResult dataSet={dataSet} mainData={mainData} doctor={availableProfessional} />
                    ))}
                </div>

                {availableProfessionals.length === 0 && <RenderEmptyBook mainData={mainData} />}
            </div>
        );
    }

    const renderSelectionBar = () => {
        const onlinePrefix = isOnlineAppointment ? 'Online ' : '';
        const treatmentsLabel = `${onlinePrefix}${Object.entries(selectedTreatments)
            .filter((selectedTreatment) => selectedTreatment[1])
            .map((selectedTreatment) => treatments[selectedLocation]?.[selectedTreatment[0]].name[0].text)
            .join(', ')}`;
        const selectionLabel = selectedChainConfig
            ? (selectedChainConfig.name[i18n.language] ?? selectedChainConfig.name.de)
            : treatmentsLabel;

        return (
            (Object.values(selectedTreatments).some((value) => value) || selectedChainConfig) && (
                <div className={classes.selectionBar}>
                    <div className={classes.selectionsWrapper}>
                        <p className={classes.selections}>{selectionLabel}</p>
                        {currentCustomer?.is_multi_treatment_booking_allowed && !selectedChainConfig && (
                            <p>{`(${
                                Object.entries(selectedTreatments).filter((selectedTreatment) => selectedTreatment[1])
                                    .length
                            })`}</p>
                        )}
                    </div>
                    <FilledButton
                        onPress={startBooking}
                        text={prismicData.accordion_continue_cta[0].text}
                        dataTestId="search-book-btn"
                    />
                </div>
            )
        );
    };

    const renderSpecialtyResults = () =>
        filteredProfessionalTypes.length > 0 && (
            <div className={classes.resultCategoryContainer}>
                <div className={classes.subTitleWrapper}>
                    <p className={classes.resultSubTitle}>{prismicData.specialty_section_title[0].text}</p>
                </div>
                <div className={classes.resultCardsContainer}>
                    {filteredProfessionalTypes.map((professionalType) => (
                        <SearchResultCard
                            key={professionalType.key[0].text}
                            cardKey={professionalType.key[0].text}
                            prismicData={professionalType}
                            searchResultType={SearchResultType.SPECIALTY}
                        />
                    ))}
                </div>
            </div>
        );

    const renderProfessionalResults = () =>
        filteredProfessionals.length > 0 && (
            <div className={classes.resultCategoryContainer}>
                <div className={classes.subTitleWrapper}>
                    <p className={classes.resultSubTitle}>{prismicData.professional_section_title[0].text}</p>
                </div>
                <div className={classes.resultCardsContainer}>
                    {filteredProfessionals.map((professional) => (
                        <SearchResultCard
                            key={professional.key[0].text}
                            cardKey={professional.key[0].text}
                            prismicData={professional}
                            searchResultType={SearchResultType.PROFESSIONAL}
                        />
                    ))}
                </div>
            </div>
        );

    const hasActiveFilter =
        isSearchActive || Boolean(selectedSpecialty) || Object.values(selectedTreatments).some(Boolean);
    const backToLanding = hasActiveFilter ? <SearchBackButton onClick={() => returnToSearchLanding(history)} /> : null;

    if (!selectedProfessional && showCategoryBrowser) {
        return (
            <div
                className={clsx(classes.container, classes.accordionContainer)}
                style={{
                    gap: 0,
                    padding: '2rem 0 0',
                    // clip does not create a scroll container, so the sticky bars inside can pin
                    overflowX: 'clip',
                }}
            >
                {backToLanding}
                <div
                    style={{
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <CategoryBrowser />
                </div>
                {isSearchActive && renderSpecialtyResults()}
                {isSearchActive && renderProfessionalResults()}
                {categoryBrowserCategories.length === 0 &&
                    (!isSearchActive ||
                        (filteredProfessionalTypes.length === 0 && filteredProfessionals.length === 0)) && (
                        <Typography className={classes.noTreatmentsHeading}>
                            {mainData.no_treatments_found[0].text}
                        </Typography>
                    )}
                {renderSelectionBar()}
            </div>
        );
    }

    if (selectedProfessional) {
        return <BookWithProfessional />;
    }

    // Handle selected treatments but no treatments due to insurance
    const hasSelectedTreatments = Object.values(selectedTreatments).some((value) => value);

    if (isXundTriageProcessed) {
        console.debug('Results:: isXundTriageProcessed', isXundTriageProcessed);
        const treatmentArray = Object.values(treatments[selectedLocation] ?? {});
        const selectedTreatment = hasSelectedTreatments
            ? treatmentArray.find((treatment) => selectedTreatments[treatment.key[0].text])
            : null;

        if (hasSelectedTreatments && filteredTreatments.length === 0) {
            if (selectedTreatment) {
                const insuranceOptions = [];
                if (selectedTreatment.public_insurance && !availableInsurances.includes(HealthInsurance.PUBLIC)) {
                    insuranceOptions.push(HealthInsurance.PUBLIC);
                }
                if (selectedTreatment.private_insurance && !availableInsurances.includes(HealthInsurance.PRIVATE)) {
                    insuranceOptions.push(HealthInsurance.PRIVATE);
                }
                if (selectedTreatment.self_payer && !availableInsurances.includes(HealthInsurance.SELF_PAYER)) {
                    insuranceOptions.push(HealthInsurance.SELF_PAYER);
                }
                const allAvailableInsurances = [...availableInsurances, ...insuranceOptions];

                return (
                    <div className={classes.container}>
                        <div className={classes.resultCategoryContainer}>
                            <div className={classes.subTitleWrapper}>
                                <p className={classes.specialtyTitle}>{prismicData.treatment_section_title[0].text}</p>
                            </div>
                            <NoTreatmentsMessage
                                selectedHealthInsurance={selectedHealthInsurance}
                                publicText={prismicData.no_treatments_available_for_public_insurance[0].text}
                                privateText={prismicData.no_treatments_available_for_private_insurance[0].text}
                                selfPayText={prismicData.no_treatments_available_for_self_payer[0].text}
                                changeInsuranceText={prismicData.change_insurance_to_see_treatments[0].text}
                                availableInsurances={allAvailableInsurances}
                            />
                        </div>
                    </div>
                );
            }
        }
    }

    if (
        !hasSelectedTreatments &&
        filteredTreatments.length +
            filteredProfessionals.length +
            filteredProfessionalTypes.length +
            filteredChainConfigs.length ===
            0
    ) {
        if (!hasActiveFilter) return null;

        return (
            <div className={classes.container}>
                <SearchNoResults onBack={() => returnToSearchLanding(history)} />
            </div>
        );
    }

    if (selectedSpecialty) {
        const noTreatments = filteredTreatments.length === 0;
        return (
            <div className={classes.container}>
                {backToLanding}
                <div className={classes.resultCategoryContainer}>
                    <div className={classes.subTitleWrapper}>
                        <p className={classes.specialtyTitle}>{prismicData.treatment_section_title[0].text}</p>
                    </div>
                    <div
                        style={
                            noTreatments
                                ? { display: 'flex' }
                                : {
                                      display: 'grid',
                                      gridTemplateColumns: `repeat(${getGridColumns()}, 1fr)`,
                                      columnGap: '1rem',
                                      rowGap: '1rem',
                                  }
                        }
                    >
                        {!noTreatments ? (
                            filteredTreatments.map((treatment) => (
                                <SearchResultCard
                                    key={treatment.key[0].text}
                                    cardKey={treatment.key[0].text}
                                    prismicData={treatment}
                                    searchResultType={SearchResultType.TREATMENT}
                                />
                            ))
                        ) : (
                            <NoTreatmentsMessage
                                selectedHealthInsurance={selectedHealthInsurance}
                                publicText={prismicData.no_treatments_available_for_public_insurance[0].text}
                                privateText={prismicData.no_treatments_available_for_private_insurance[0].text}
                                selfPayText={prismicData.no_treatments_available_for_self_payer[0].text}
                                changeInsuranceText={prismicData.change_insurance_to_see_treatments[0].text}
                                availableInsurances={availableInsurances}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            {backToLanding}
            {renderSpecialtyResults()}

            {(filteredTreatments.length > 0 || filteredChainConfigs.length > 0) && (
                <div className={classes.resultCategoryContainer}>
                    <div className={classes.subTitleWrapper}>
                        <p className={classes.resultSubTitle}>{prismicData.treatment_section_title[0].text}</p>
                    </div>
                    <div
                        className={classes.resultCardsContainer}
                        style={{
                            // Always a grid: the rows fill their column, so an even grid is what keeps
                            // every card the same width instead of wrapping to whatever fits.
                            display: 'grid',
                            gridTemplateColumns: `repeat(${getGridColumns()}, 1fr)`,
                            columnGap: '1rem',
                            rowGap: '1rem',
                        }}
                    >
                        {filteredTreatments.map((treatment) => (
                            <SearchResultCard
                                key={treatment.key[0].text}
                                cardKey={treatment.key[0].text}
                                prismicData={treatment}
                                searchResultType={SearchResultType.TREATMENT}
                            />
                        ))}
                        {renderChainCards()}
                    </div>
                </div>
            )}

            {renderProfessionalResults()}
            {currentCustomer?.is_accordion_view_active &&
                !currentCustomer?.is_category_view_active &&
                currentCustomer?.is_multi_treatment_booking_allowed &&
                renderSelectionBar()}
        </div>
    );
};

export default SearchResults;
